// src/Dashboard/components/DashboardTopbar.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaMoon,
  FaSun,
  FaSignOutAlt,
  FaGlobeEurope,
  FaHome,
  FaExchangeAlt,
  FaSearch,
  FaTimes,
  FaEnvelope,
  FaBell,
} from "react-icons/fa";

import { MdOutlineMessage } from "react-icons/md";

import { useUser } from "../../pages/Home/UserContext";
// import { useMessage } from "../../hooks/MessageContext";
import { getSettings } from "../../services/settingsServices";

import {
  getActiveDashboardItem,
  getVisibleDashboardSections,
  unwrapSettings,
} from "../config/dashboardMenuConfig";

const API_BASE = process.env.REACT_APP_API_URL || "";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (isExternalURL(url)) return url;

  const cleanBase = API_BASE.replace(/\/$/, "");
  const cleanPath = String(url).startsWith("/") ? url : `/${url}`;

  return `${cleanBase}${cleanPath}`;
};

const normalizeSearch = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const formatUnreadBadge = (count) => {
  const value = Number(count || 0);
  if (value <= 0) return null;
  return value > 99 ? "99+" : String(value);
};

const isMessagingMenuItem = (item = {}) => {
  const raw = normalizeSearch(`${item.title || ""} ${item.path || ""}`);

  return (
    raw.includes("messagerie") ||
    raw.includes("message") ||
    raw.includes("messages")
  );
};

function getAvatarSrc(user) {
  const photo =
    user?.photoURL ||
    user?.photo_url ||
    user?.avatar ||
    user?.image_url ||
    null;

  return toAbsoluteUrl(photo) || "/img/user_avatar.jpg";
}

const isNotificationMenuItem = (item = {}) => {
  const raw = normalizeSearch(`${item.title || ""} ${item.path || ""}`);

  return raw.includes("notification") || raw.includes("notifications");
};

const isContactFormMenuItem = (item = {}) => {
  const raw = normalizeSearch(`${item.title || ""} ${item.path || ""}`);

  return (
    raw.includes("contact") ||
    raw.includes("formulaire") ||
    raw.includes("message formulaire") ||
    raw.includes("messages formulaire")
  );
};

const isAdminUser = (user = {}) => {
  const roleId = Number(
    user?.role_id ??
      user?.roleId ??
      user?.id_role ??
      user?.role ??
      user?.roles_id ??
      0,
  );

  const roleLabel = normalizeSearch(
    `${user?.role_label || ""} ${user?.roleName || ""} ${user?.role_name || ""}`,
  );

  return (
    user?.isAdmin === true ||
    user?.admin === true ||
    roleId === 1 ||
    roleLabel.includes("administrateur")
  );
};

function DashboardCounterBadge({ count }) {
  const badge = formatUnreadBadge(count);

  if (!badge) return null;

  return (
    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold leading-none text-white shadow ring-2 ring-white dark:ring-slate-950">
      {badge}
    </span>
  );
}

function DashboardIconButton({
  title,
  onClick,
  children,
  count,
  bump,
  tone = "default",
}) {
  const tones = {
    default:
      "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    message:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20",
    notification:
      "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20",
    contact:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border shadow-sm transition ${
        tones[tone] || tones.default
      }`}
      title={title}
      aria-label={title}
    >
      <span className={bump ? "animate-bell-shake" : ""}>{children}</span>
      <DashboardCounterBadge count={count} />
    </button>
  );
}

export default function DashboardTopbar({
  unreadCount = 0,
  unreadMessageCount = 0,
  unreadCountContactForm = 0,
  bumpMessages = false,
  bumpNotifications = false,
  bumpContactForms = false,
}) {
  const { user, updateUser } = useUser();
  // const { unreadMessageCount: unreadMessageCountFromContext } = useMessage();

  // const unreadMessageCount =
  //   unreadMessageCountFromProps ?? unreadMessageCountFromContext ?? 0;

  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  const [settings, setSettings] = useState({});
  const [mounted, setMounted] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [theme, setTheme] = useState(
    document.documentElement.classList.contains("dark") ? "dark" : "light",
  );

  useEffect(() => {
    setMounted(true);

    const storedUserInfo = localStorage.getItem("userInfo");

    if (storedUserInfo && !user) {
      try {
        updateUser(JSON.parse(storedUserInfo));
      } catch (error) {
        console.error("Erreur lors du parsing userInfo", error);
      }
    }
  }, [user, updateUser]);

  useEffect(() => {
    let mountedSettings = true;

    (async () => {
      try {
        const response = await getSettings();
        const cleanSettings = unwrapSettings(response);

        if (mountedSettings) {
          setSettings(cleanSettings || {});
        }
      } catch (error) {
        console.error("Erreur chargement logo club dashboard :", error);

        if (mountedSettings) {
          setSettings({});
        }
      }
    })();

    return () => {
      mountedSettings = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!searchRef.current) return;
      if (!searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const activeItem = useMemo(
    () => getActiveDashboardItem(location.pathname),
    [location.pathname],
  );

  const visibleSections = useMemo(() => {
    return getVisibleDashboardSections(user, settings);
  }, [user, settings]);

  const searchablePages = useMemo(() => {
    return visibleSections.flatMap((section) =>
      (section.items || []).map((item) => ({
        ...item,
        sectionLabel: section.label,
        searchLabel: normalizeSearch(`${section.label} ${item.title}`),
      })),
    );
  }, [visibleSections]);

  const messagingItem = useMemo(() => {
    return searchablePages.find((item) => isMessagingMenuItem(item));
  }, [searchablePages]);

  const notificationItem = useMemo(() => {
    return searchablePages.find((item) => isNotificationMenuItem(item));
  }, [searchablePages]);

  const contactFormItem = useMemo(() => {
    return searchablePages.find((item) => isContactFormMenuItem(item));
  }, [searchablePages]);

  const isAdmin = isAdminUser(user);

  const unreadBadge = formatUnreadBadge(unreadMessageCount);

  const searchResults = useMemo(() => {
    const q = normalizeSearch(searchValue);

    if (!q) {
      return searchablePages.slice(0, 8);
    }

    const tokens = q.split(/\s+/).filter(Boolean);

    return searchablePages
      .filter((item) =>
        tokens.every((token) => item.searchLabel.includes(token)),
      )
      .slice(0, 10);
  }, [searchValue, searchablePages]);

  const pageTitle = activeItem?.title || "Dashboard";
  const classicPath = activeItem?.classicPath || "/mon-espace";

  const handleLogout = () => {
    localStorage.removeItem("userInfo");
    localStorage.removeItem("token");
    updateUser(null);
    navigate("/login");
  };

  const toggleTheme = () => {
    const nextTheme = document.documentElement.classList.toggle("dark")
      ? "dark"
      : "light";

    localStorage.setItem("theme", nextTheme);
    setTheme(nextTheme);
  };

  const goToPublicSite = () => navigate("/");

  const goToDashboardHome = () => {
    navigate("/dashboard/tableau-de-bord");
  };

  const goToClassicView = () => {
    localStorage.setItem("mac_preferred_view", "classic");
    navigate(classicPath);
  };

  const goToProfile = () => navigate("/profile");

  const goToMessaging = () => {
    navigate(messagingItem?.path || "/dashboard/messagerie");
  };

  const goToNotifications = () => {
    navigate(notificationItem?.path || "/dashboard/tableau-de-bord");
  };

  const goToContactForms = () => {
    navigate(contactFormItem?.path || "/dashboard/tableau-de-bord");
  };

  const goToDashboardPage = (item) => {
    if (!item?.path) return;

    navigate(item.path);
    setSearchOpen(false);
    setSearchValue("");
  };

  const avatarSrc = `${getAvatarSrc(user)}?v=${
    user?.photoURL || user?.photo_url || "default"
  }`;

  const clubLogoSrc = toAbsoluteUrl(settings?.logo_club);

  return (
    <header className="fixed inset-x-0 top-0 z-[60] h-[82px] border-b border-slate-200/80 bg-white/90 backdrop-blur-xl dark:border-slate-800/80 dark:bg-slate-950/90">
      <div className="mx-auto flex h-full max-w-[1900px] items-center justify-between gap-3 px-4 sm:px-5 lg:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={goToDashboardHome}
            className="hidden h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:flex"
            title="Retour au dashboard"
          >
            {clubLogoSrc ? (
              <img
                src={clubLogoSrc}
                alt="Logo du club"
                className="h-full w-full object-contain p-1.5"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <FaHome className="text-slate-700 dark:text-slate-200" />
            )}
          </button>

          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400 dark:text-slate-500">
              Espace connecté
            </p>

            <h1 className="truncate text-base font-bold text-slate-900 sm:text-lg dark:text-white">
              {pageTitle}
            </h1>

            <p className="hidden text-xs text-slate-500 dark:text-slate-400 md:block">
              Tableau de bord du club
            </p>
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <div
            ref={searchRef}
            className="relative hidden min-w-[260px] max-w-[430px] flex-1 lg:block"
          >
            <label htmlFor="dashboard-page-search" className="sr-only">
              Rechercher une page du dashboard
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                id="dashboard-page-search"
                type="text"
                value={searchValue}
                onFocus={() => setSearchOpen(true)}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  setSearchOpen(true);
                }}
                placeholder="Rechercher une page..."
                className="h-10 w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-10 text-sm font-medium text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:placeholder:text-slate-500"
              />

              {searchValue ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchValue("");
                    setSearchOpen(true);
                  }}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                  aria-label="Effacer la recherche"
                  title="Effacer"
                >
                  <FaTimes className="text-xs" />
                </button>
              ) : null}
            </div>

            {searchOpen ? (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-[90] w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                    Navigation rapide
                  </p>
                </div>

                {searchResults.length > 0 ? (
                  <div className="max-h-[360px] overflow-y-auto p-2">
                    {searchResults.map((item) => (
                      <button
                        key={item.path}
                        type="button"
                        onClick={() => goToDashboardPage(item)}
                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                          {item.icon || <FaSearch />}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white font-montserrat">
                            {item.title}
                          </span>

                          <span className="mt-0.5 block truncate text-xs text-slate-500 dark:text-slate-400">
                            {item.sectionLabel} | {item.title}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    Aucune page trouvée.
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={goToClassicView}
            className="hidden h-10 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 xl:flex"
            title="Basculer vers la vue classique"
          >
            <FaExchangeAlt />
            Vue classique
          </button>

          <button
            type="button"
            onClick={goToPublicSite}
            className="hidden h-10 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 sm:flex"
            title="Voir le site public"
          >
            <FaGlobeEurope />
            Site public
          </button>

          {user ? (
            <>
              <DashboardIconButton
                title={
                  unreadMessageCount > 0
                    ? `${unreadMessageCount} message(s) non lu(s)`
                    : "Messagerie"
                }
                onClick={goToMessaging}
                count={unreadMessageCount}
                bump={bumpMessages}
                tone="message"
              >
                <FaEnvelope />
              </DashboardIconButton>

              <DashboardIconButton
                title={
                  unreadCount > 0
                    ? `${unreadCount} notification(s) non lue(s)`
                    : "Notifications"
                }
                onClick={goToNotifications}
                count={unreadCount}
                bump={bumpNotifications}
                tone="notification"
              >
                <FaBell />
              </DashboardIconButton>

              {isAdmin ? (
                <DashboardIconButton
                  title={
                    unreadCountContactForm > 0
                      ? `${unreadCountContactForm} message(s) du formulaire non lu(s)`
                      : "Messages du formulaire"
                  }
                  onClick={goToContactForms}
                  count={unreadCountContactForm}
                  bump={bumpContactForms}
                  tone="contact"
                >
                  <MdOutlineMessage />
                </DashboardIconButton>
              ) : null}
            </>
          ) : null}

          <button
            type="button"
            onClick={toggleTheme}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            title="Changer le thème"
          >
            {mounted && theme === "dark" ? <FaSun /> : <FaMoon />}
          </button>

          {user ? (
            <button
              type="button"
              onClick={goToProfile}
              className="flex shrink-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white py-1.5 pl-2 pr-3 text-left shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
              title="Profil utilisateur"
            >
              <img
                src={avatarSrc}
                alt="Avatar utilisateur"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-200 dark:ring-slate-700"
              />

              <div className="hidden min-w-0 sm:block">
                <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                  {user?.prenom || user?.first_name || ""}{" "}
                  {user?.nom || user?.last_name || ""}
                </div>

                <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                  Profil
                </div>
              </div>
            </button>
          ) : null}

          {user ? (
            <button
              type="button"
              onClick={handleLogout}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-red-200 bg-red-50 text-red-700 shadow-sm transition hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
              title="Déconnexion"
            >
              <FaSignOutAlt />
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
